import React, { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, Maximize2, Minimize2, Save, ZoomIn, ZoomOut } from "lucide-react";
import ConnectionPreview from "../../components/studio/ConnectionPreview";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

// UI Components
const Label = ({
  children,
  className,
}: {
  children?: React.ReactNode;
  className?: string;
}) => (
  <label
    className={cn(
      "block text-xs text-muted-foreground mb-1.5 font-medium",
      className
    )}
  >
    {children}
  </label>
);

const DataRow = ({
  label,
  value,
  unit,
}: {
  label: string;
  value: string | number;
  unit: string;
}) => (
  <div className="flex justify-between items-center py-1 border-b border-border/50 last:border-0">
    <span className="text-muted-foreground text-sm">{label}</span>
    <span className="font-mono text-sm text-foreground font-medium text-right">
      {value} <span className="text-muted-foreground text-xs">{unit}</span>
    </span>
  </div>
);

// Helper for native select with ShadCN styling
const NativeSelect = React.forwardRef<
  HTMLSelectElement,
  React.SelectHTMLAttributes<HTMLSelectElement>
>(({ className, ...props }, ref) => {
  return (
    <div className="relative">
      <select
        className={cn(
          "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 appearance-none pr-8",
          className
        )}
        ref={ref}
        {...props}
      />
      <ChevronDown className="absolute right-3 top-2.5 h-4 w-4 opacity-50 pointer-events-none" />
    </div>
  );
});
NativeSelect.displayName = "NativeSelect";

// --- TYPES ---
interface Bracket { id: string; name: string; h: number; w: number; t: number; d: number; }
interface Bolt { id: string; name: string; d: number; grade: string; }
interface Pin { id: string; name: string; d: number; }
interface Tab { id: string; name: string; w: number; t: number; }

// --- MOCK DATA ---
const MOCK_BRACKETS: Bracket[] = [
    { id: '1', name: '1.5x1.5x12ga (0.1046) Bent Sheet', h: 1.5, w: 1.5, t: 0.1046, d: 6 },
  {
    id: "2",
    name: "1.5x1.5x7ga (0.1793) Bent Sheet",
    h: 1.5,
    w: 1.5,
    t: 0.1793,
    d: 6,
  },
  {
    id: "3",
    name: "3x1.5x12ga (0.1046) Bent Sheet",
    h: 3,
    w: 1.5,
    t: 0.1046,
    d: 6,
  },
  {
    id: "4",
    name: "3x1.5x7ga (0.1793) Bent Sheet",
    h: 3,
    w: 1.5,
    t: 0.1793,
    d: 6,
  },
  {
    id: "5",
    name: "1.5x1.5x10ga (0.1345) Bent Sheet",
    h: 1.5,
    w: 1.5,
    t: 0.1345,
    d: 6,
  },
  {
    id: "6",
    name: "3x1.5x10ga (0.1345) Bent Sheet",
    h: 3,
    w: 1.5,
    t: 0.1345,
    d: 6,
  },
];

const MOCK_BOLTS: Bolt[] = [
  { id: "b1", name: "0.38'', Grade 5", d: 0.38, grade: "5" },
  { id: "b2", name: "0.5'', Grade 5", d: 0.5, grade: "5" },
  { id: "b3", name: "0.5'', Grade 8", d: 0.5, grade: "8" },
  { id: "b4", name: "0.625'', Grade 5", d: 0.625, grade: "5" },
];

const MOCK_PINS: Pin[] = [
  { id: "p1", name: "0.39'', C1010 Rivet", d: 0.39 },
  { id: "p2", name: "0.5'', C1010 Rivet", d: 0.5 },
];

const MOCK_TABS: Tab[] = [
  { id: "t1", name: "0.5'' Tab", w: 0.5, t: 0.1793 },
  { id: "t2", name: "1.0'' Tab", w: 1.0, t: 0.1793 },
];

const ConnectionBuilder = () => {
  // --- STATE ---
  const [name, setName] = useState("");
  const [ed1, setEd1] = useState(1);
  const [ed2, setEd2] = useState(1);
  const [ed3, setEd3] = useState(0.5);

  const [bracketId, setBracketId] = useState(MOCK_BRACKETS[1].id);
  const [spacing, setSpacing] = useState(4);
  const [openHoles, setOpenHoles] = useState("No");
  const [bhs, setBhs] = useState(1);

  const [connectionType, setConnectionType] = useState("Bolt");
  const [connectorId, setConnectorId] = useState(MOCK_BOLTS[1].id);
  const [totalN, setTotalN] = useState(2);
  const [beamType, setBeamType] = useState("All");

  const [partDataOpen, setPartDataOpen] = useState(true);

  // Preview Scale & Screen State
  const [zoom, setZoom] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 3));
  const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.5));

  // --- COMPUTED ---
  const selectedBracket = useMemo(
    () => MOCK_BRACKETS.find((b) => b.id === bracketId) || MOCK_BRACKETS[0],
    [bracketId]
  );

  const connectorDetails = useMemo(() => {
    if (connectionType === "Bolt")
      return MOCK_BOLTS.find((b) => b.id === connectorId);
    if (connectionType === "Pin")
      return MOCK_PINS.find((p) => p.id === connectorId);
    if (connectionType === "Tab")
      return MOCK_TABS.find((t) => t.id === connectorId);
    return null;
  }, [connectionType, connectorId]);

  // Handle Type Change
  const handleTypeChange = (newType: string) => {
    setConnectionType(newType);
    // Reset connector ID to first of new type
    if (newType === "Bolt") setConnectorId(MOCK_BOLTS[0].id);
    else if (newType === "Pin") setConnectorId(MOCK_PINS[0].id);
    else if (newType === "Tab") setConnectorId(MOCK_TABS[0].id);
  };

  return (
    <div className="flex-1 bg-background h-full flex flex-col font-sans">
      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden h-[calc(100vh-64px)]">
        {/* LEFT PANEL: Form */}
        <div className="w-full lg:w-[500px] lg:min-w-[400px] lg:max-w-[50%] bg-card border-r border-border overflow-y-auto flex flex-col custom-scrollbar h-1/2 lg:h-full shrink-0">
          <div className="px-6 pt-6 pb-2">
            <h2 className="text-xl font-medium text-muted-foreground mb-6">
              The connection
            </h2>
          </div>

          <div className="px-6 pb-10 space-y-6">
            {/* Name & Edge Distances */}
            <div className="grid grid-cols-12 gap-5">
              <div className="col-span-12">
                <Label>Connection name</Label>
                <Input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="col-span-4">
                <Label>Edge distance, ED1 (in)</Label>
                <Input
                  type="number"
                  value={ed1}
                  onChange={(e) => setEd1(parseFloat(e.target.value))}
                />
              </div>
              <div className="col-span-4">
                <Label>Edge distance, ED2 (in)</Label>
                <Input
                  type="number"
                  value={ed2}
                  onChange={(e) => setEd2(parseFloat(e.target.value))}
                />
              </div>
              <div className="col-span-4">
                <Label>Edge distance, ED3 (in)</Label>
                <Input
                  type="number"
                  value={ed3}
                  onChange={(e) => setEd3(parseFloat(e.target.value))}
                />
              </div>
            </div>

            {/* Bracket & Spacing */}
            <div className="grid grid-cols-12 gap-5">
              <div className="col-span-12">
                <Label>Bracket part</Label>
                <NativeSelect
                  value={bracketId}
                  onChange={(e) => setBracketId(e.target.value)}
                >
                  <option value="create">+ Create new</option>
                  {MOCK_BRACKETS.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                    </option>
                  ))}
                </NativeSelect>
              </div>
              <div className="col-span-6">
                <Label>Spacing, S (in)</Label>
                <Input
                  type="number"
                  value={spacing}
                  onChange={(e) => setSpacing(parseFloat(e.target.value))}
                />
              </div>
              <div className="col-span-6">
                <Label>Open holes?</Label>
                <NativeSelect
                  value={openHoles}
                  onChange={(e) => setOpenHoles(e.target.value)}
                >
                  <option value="No">No</option>
                  <option value="Yes">Yes</option>
                </NativeSelect>
              </div>
            </div>

            {/* Conditional Hole Spacing */}
            {openHoles === "Yes" && (
              <div className="grid grid-cols-12 gap-5">
                <div className="col-span-6"></div>
                <div className="col-span-6">
                  <Label>Bracket hole spacing, BHS (in)</Label>
                  <Input
                    type="number"
                    value={bhs}
                    onChange={(e) => setBhs(parseFloat(e.target.value))}
                  />
                </div>
              </div>
            )}

            {/* Connection Type & Connectors */}
            <div className="grid grid-cols-12 gap-5">
              <div className="col-span-6">
                <Label>Connection type</Label>
                <NativeSelect
                  value={connectionType}
                  onChange={(e) => handleTypeChange(e.target.value)}
                >
                  <option value="Bolt">Bolt</option>
                  <option value="Pin">Pin</option>
                  <option value="Tab">Tab</option>
                </NativeSelect>
              </div>
              <div className="col-span-6">
                <Label>Total, N</Label>
                <NativeSelect
                  value={totalN}
                  onChange={(e) => setTotalN(parseInt(e.target.value))}
                >
                  <option value={2}>2</option>
                  <option value={3}>3</option>
                  <option value={4}>4</option>
                  <option value={5}>5</option>
                </NativeSelect>
              </div>
              <div className="col-span-12">
                <Label>{connectionType} part</Label>
                <NativeSelect
                  value={connectorId}
                  onChange={(e) => setConnectorId(e.target.value)}
                >
                  {connectionType === "Bolt" &&
                    MOCK_BOLTS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  {connectionType === "Pin" &&
                    MOCK_PINS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                  {connectionType === "Tab" &&
                    MOCK_TABS.map((b) => (
                      <option key={b.id} value={b.id}>
                        {b.name}
                      </option>
                    ))}
                </NativeSelect>
              </div>
            </div>

            {/* Beam Types */}
            <div className="grid grid-cols-12 gap-5">
              <div className="col-span-12">
                <Label>Applicable beam types</Label>
                <NativeSelect
                  value={beamType}
                  onChange={(e) => setBeamType(e.target.value)}
                >
                  <option value="All">All</option>
                  <option value="Hot rolled only">Hot rolled only</option>
                  <option value="Roll formed only">Roll formed only</option>
                </NativeSelect>
              </div>
            </div>

            <div className="border-t border-border my-6"></div>

            {/* Part Details Accordion */}
            <div>
              <button
                className="flex items-center justify-between w-full text-left mb-6 focus:outline-none group"
                onClick={() => setPartDataOpen(!partDataOpen)}
              >
                <h3 className="text-primary font-medium text-base">Part</h3>
                {partDataOpen ? (
                  <ChevronUp size={20} className="text-primary/70" />
                ) : (
                  <ChevronDown size={20} className="text-primary/70" />
                )}
              </button>

              {partDataOpen && (
                <div className="grid grid-cols-2 gap-x-12 gap-y-1">
                  {/* Bracket Data */}
                  <div>
                    <DataRow
                      label="Bracket height, H:"
                      value={selectedBracket.h}
                      unit="(in)"
                    />
                    <DataRow
                      label="Bracket width, B:"
                      value={selectedBracket.w}
                      unit="(in)"
                    />
                    <DataRow
                      label="Bracket thickness, T:"
                      value={selectedBracket.t}
                      unit="(in)"
                    />
                    <DataRow
                      label="Bracket depth, D:"
                      value={selectedBracket.d}
                      unit="(in)"
                    />
                  </div>

                  {/* Connector Data */}
                  <div>
                    {connectionType === "Bolt" && (
                      <>
                        <DataRow
                          label="Bolt diameter, d:"
                          value={(connectorDetails as Bolt)?.d || 0}
                          unit="(in)"
                        />
                        <DataRow
                          label="Bracket hole diameter, dh:"
                          value={
                            (connectorDetails as Bolt)?.d
                              ? (connectorDetails as Bolt).d + 0.0625
                              : 0
                          }
                          unit="(in)"
                        />
                      </>
                    )}
                    {connectionType === "Pin" && (
                      <DataRow
                        label="Pin diameter, d:"
                        value={(connectorDetails as Pin)?.d || 0}
                        unit="(in)"
                      />
                    )}
                    {connectionType === "Tab" && (
                      <>
                        <DataRow
                          label="Tab width, ttab:"
                          value={(connectorDetails as Tab)?.w || 0}
                          unit="(in)"
                        />
                        <DataRow
                          label="Tab thickness, ttab:"
                          value={(connectorDetails as Tab)?.t || 0}
                          unit="(in)"
                        />
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT PANEL: Preview */}
        <div className={cn(
            "flex-1 bg-muted/30 flex flex-col relative overflow-hidden transition-all duration-300",
            isFullscreen ? "fixed inset-0 z-50 bg-background" : "h-1/2 lg:h-full"
        )}>
            
             {/* Watermark / Background Grid */}
            <div className="absolute inset-0 pointer-events-none opacity-[0.4]"
                 style={{
                    backgroundImage: 'radial-gradient(circle, #a1a1aa 1px, transparent 1px)',
                    backgroundSize: '24px 24px'
                 }}
            />

            {/* Floating Toolbar */}
            <div className="absolute top-6 right-6 flex flex-col gap-2 z-10">
                <Button 
                    variant="secondary" size="icon" 
                    className="h-9 w-9 shadow-sm bg-background/80 backdrop-blur border border-border/50 hover:bg-background"
                    onClick={() => setIsFullscreen(!isFullscreen)}
                >
                     {isFullscreen ? <Minimize2 className="h-4 w-4 text-muted-foreground" /> : <Maximize2 className="h-4 w-4 text-muted-foreground" />}
                </Button>
                <div className="flex flex-col rounded-md shadow-sm bg-background/80 backdrop-blur border border-border/50 overflow-hidden">
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-none hover:bg-muted" onClick={handleZoomIn}>
                        <ZoomIn className="h-4 w-4 text-muted-foreground" />
                    </Button>
                    <div className="h-[1px] bg-border/50 mx-2" />
                    <Button variant="ghost" size="icon" className="h-9 w-9 rounded-none hover:bg-muted" onClick={handleZoomOut}>
                        <ZoomOut className="h-4 w-4 text-muted-foreground" />
                    </Button>
                </div>
            </div>

            {/* Main Canvas */}
            <div className="flex-1 flex items-center justify-center p-8 md:p-12 min-h-0 container mx-auto relative z-0 overflow-hidden">
                 <div 
                    className="w-full h-full max-h-[600px] flex items-center justify-center bg-background/40 backdrop-blur-sm rounded-xl border border-secondary/20 shadow-sm p-4 transition-transform duration-200 ease-out"
                    style={{ transform: `scale(${zoom})` }}
                 >
                    <ConnectionPreview 
                        n={totalN} 
                        s={spacing} 
                        ed1={ed1} 
                        ed2={ed2} 
                    />
                </div>
            </div>

            {/* Bottom Actions Bar */}
            <div className="p-6 border-t border-border bg-background/80 backdrop-blur supports-[backdrop-filter]:bg-background/60 flex items-center justify-between gap-4">
                <div className="hidden md:flex flex-col">
                     <span className="text-xs font-medium text-foreground">Configuration Preview</span>
                     <span className="text-[10px] text-muted-foreground">{totalN} {connectionType}s • Spacing: {spacing}" • Scale: {(zoom * 100).toFixed(0)}%</span>
                </div>
                {isFullscreen && (
                     <Button variant="outline" onClick={() => setIsFullscreen(false)}>Close Fullscreen</Button>
                )}
                <Button className="w-full md:w-auto min-w-[160px] text-sm py-5 shadow-lg shadow-primary/20">
                    <Save className="mr-2 h-4 w-4" />
                    Save connection
                </Button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default ConnectionBuilder;
